import React, { useState, useEffect, useRef, useCallback } from "react";
import "./AddEntryEmployee.css";
import { Typography, Sheet, Box, Button, Link as JoyLink, CircularProgress, CssVarsProvider } from "@mui/joy";
// NOTE: Please ensure your type definitions in '.../utils/types' are updated as described below.
import { AddNewEntryProps, FormDataState } from "../../utils/types";
import { billCategoryInvoiceOptions, billTypeOptions, muiThemeFinal, procurementTypeOptions, throughGEMOptions } from "../../utils/constants";
import { SelectInput, TextareaInput, TextInput } from "../../utils/input";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ThemeProvider as MaterialThemeProvider } from "@mui/material/styles";

import {
  useGetInvoiceDetailsQuery,
  useHandleInvoiceEntryMutation,
  useUploadForExtractionMutation,
  useLazyGetExtractionStatusQuery,
  useLazyGetExtractionResultQuery,
  useGetTredsPaymentsDetailsQuery,
  useGetTredsListOdataQuery,
} from "../../services/invoices/InvoiceApiSlice";
import ExtractionReviewModal, { ExtractionValues } from "./ExtractionReviewModal";
import PoSelectionDropdown from "../PoSelectionDropdown";
import { toast } from "react-toastify";
import Sidebar from "../VendorSidebar";
import { CssBaseline } from "@mui/material";
import useAddEntryForm from "../../hooks/useAddEntryForm";
import SuccessModal from "../Common/SuccessModal";
import AttachmentTable from "./AttachmentTable";
import { formatDateForInput, formatIndianCurrency } from "../../utils/utils";
import BtsFinancialDetailsSection from "./BtsFinancialDetailsSection";
import LocationDropdown from "./LocationDropdown";
import DivisionDropdown from "./DivisionDropdown";
import { useGetVendorDetailsWithIdQuery } from "../../services/vendor/VendorApi";
import VendorInfoCard from "../vendor-details/VendorInfoCard";
import OtherAttachments from "./OtherAttachments";
import { fileToBase64 } from "../../utils/files";
import FeedbackModals from "../Employee/ApproverPage/FeedbackModals";

const getInvoiceIdFromState = (locationState: any): string | null => {
  const bts_no = locationState?.invoiceData?.bts_no;
  if (typeof bts_no === "string" && bts_no.includes("-")) {
    return bts_no.split("-")[0];
  }
  return null;
};

export const AddEntry: React.FC<AddNewEntryProps> = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const [isOpenFeedbackModal, setIsOpenFeedbackModal] = useState(false);
  var { id } = useParams();
  // id = atob(atob(id ?? ''));
  // const {data: vendor_details} = useGetVendorDetailsQuery(undefined, {refetchOnMountOrArgChange: true});
  const current_vendor_id: any = localStorage.getItem("vendor_code");
  const { data: vendor_details } = useGetVendorDetailsWithIdQuery(current_vendor_id);

  const responseVendorData = {
    companyName: vendor_details?.name || "",
    vendorNo: vendor_details?.lifnr || "",
    address: vendor_details?.streetHouse || "",
    panNoLabel: "Pan No.",
    panNoValue: vendor_details?.pan || "",
    bankKeyLabel: "Bank Key",
    bankKeyValue: vendor_details?.bankKey || "",
    gstLabel: "GST",
    gstValue: vendor_details?.taxNumber || "",
    accountNoLabel: "A/C",
    accountNoValue: vendor_details?.bankAccount || "",
  };

  const [successModalOpen, setSuccessModalOpen] = useState<{
    open: boolean;
    title?: string;
    description?: string;
  }>({
    open: false,
    title: "",
    description: "",
  });

  const { validateForm, initialDocumentsData, sectionHeaderStyles, handleFileDownload, handleCurrencyOnBlur } = useAddEntryForm();

  const isEditMode = id ? true : false;
  const isReadOnly = location.state?.isReadOnly ?? false;
  const invoiceId = isEditMode ? getInvoiceIdFromState(location.state) : null;
  //const isReadOnly = id ? true: false;


  const {
    data: invoiceDetails,
    isLoading: isLoadingDetails,
    isError: isDetailsError,
  } = useGetInvoiceDetailsQuery(id, { skip: !id });

  const [handleInvoiceEntry, { isLoading: isSubmitting }] = useHandleInvoiceEntryMutation();
  // const [handleEmployeeInvoiceEntry, { isLoading: isSubmittingEmployeeEntry }] = useHandleEmployeeInvoiceEntryMutation();

  // ── Smart Invoice Extraction ───────────────────────────────────────────
  const [uploadForExtraction] = useUploadForExtractionMutation();
  const [fetchExtractionStatus] = useLazyGetExtractionStatusQuery();
  const [fetchExtractionResult] = useLazyGetExtractionResultQuery();

  const [extractState, setExtractState] = useState<{
    status: "idle" | "uploading" | "done" | "failed";
    extractedData: { fields: Record<string, any>; confidence: Record<string, any>; original_filename?: string } | null;
    pdfFile: File | null;
    modalOpen: boolean;
    errorMsg: string | null;
  }>({
    status: "idle",
    extractedData: null,
    pdfFile: null,
    modalOpen: false,
    errorMsg: null,
  });

  const [trendsCodeList, setTrendsCodeList] = useState<any>([]);

  const { data: tradsPaymentist } = useGetTredsPaymentsDetailsQuery(localStorage.getItem("vendor_code"), { skip: localStorage.getItem("vendor_code") === '' });

  useEffect(() => {
    if (tradsPaymentist) {
      let arr1 = [];
      tradsPaymentist?.d?.results?.map((item) => arr1.push({ value: item?.Ztresdcode, label: item?.Ztresdcode }))
      setTrendsCodeList([...arr1]);

      setTimeout(() => {
        console.log(invoiceDetails?.treds_payment)
        setFormData((prev) => ({ ...prev, treds_payment: invoiceDetails?.treds_payment ?? "" }))
      }, 1000);
    }
  }, [tradsPaymentist]);
  // Prefill form from extraction modal confirmed values
  const handleExtractionConfirm = useCallback((values: ExtractionValues) => {
    setFormData((prev) => ({
      ...prev,
      billNo: values.invoice_number,
      billDate: values.invoice_date,
      grossAmount: values.base_amount,
      totalTax: values.total_tax,
      billAmount: values.invoice_amount,
      netPayable: values.net_payable,
    }));
    setExtractState((prev) => ({ ...prev, modalOpen: false }));
    toast.success("Invoice details prefilled from extracted data.");
  }, []);

  const handleExtractionCancel = useCallback(() => {
    setExtractState((prev) => ({ ...prev, modalOpen: false }));
  }, []);

  // FIX: Added properties to hold existing file names from the server.
  const baseInitialState: any = {
    attachment: null,
    existingAttachmentName: null, // For edit mode
    attachmentPreview: null,
    throughGEM: "",
    gemContactNo: "",
    billType: "",
    billCategory: "",
    procurementType: "",
    treds_payment: "",
    poContract: "",
    pending_name: '',
    dakNo: "",
    invoiceForWhichLocation: "",
    divisionName: "",
    remarks: "",
    btsNo: "System Generated",
    btsEntryDate: new Date().toISOString().slice(0, 10),
    grossAmount: "",
    billDate: "",
    totalTax: "",
    billAmount: "",
    billNo: "",
    advanceAdj: "",
    netPayable: "",
    documents: initialDocumentsData.map((doc) => ({ ...doc })),
    poContractDetails: {
      plant: "",
      created_by: "",
      pending_with: "",
      responsible_person: "",
    },
    approver_remarks: invoiceDetails?.approver_remarks || "",
    location_name: "",
    approver_email: "",
    othersAttachments: []
  };

  const [formData, setFormData] = useState<FormDataState>({
    ...baseInitialState,
  });

  const { data: tradsListOdata } = useGetTredsListOdataQuery(formData?.poContractDetails?.ebeln, { skip: !formData?.poContractDetails?.ebeln });
  useEffect(() => {
    if (isEditMode && invoiceDetails) {
      const createDataUrl = (data: string | null, mime: string | null): string | null => {
        return data && mime ? `data:${mime};base64,${data}` : null;
      };

      // FIX: This object now correctly maps API string names to the `existing...` properties,
      // preventing the TypeScript error.
      const commonDataFromApi = {
        remarks: invoiceDetails.Remarks ?? "",
        throughGEM: invoiceDetails.gem_option ?? "",
        gemContactNo: invoiceDetails.gem_contact ?? "",
        advanceAdj: invoiceDetails.advance_adj ?? "",
        billAmount: invoiceDetails.bill_amount ?? "",
        billCategory: invoiceDetails.bill_category ?? "",
        billDate: formatDateForInput(invoiceDetails.bill_date),
        billType: invoiceDetails.bill_type ?? "",
        btsEntryDate: formatDateForInput(invoiceDetails.bts_entry_date),
        btsNo: invoiceDetails.bts_no ?? "System Generated",
        dakNo: invoiceDetails.dak_no ?? "",
        divisionName: invoiceDetails.division_name ?? "",
        grossAmount: invoiceDetails.gross_amount ?? "",
        invoiceForWhichLocation: invoiceDetails.invoice_location ?? "",
        netPayable: invoiceDetails.net_payable ?? "",
        poContract: invoiceDetails.po_contract ?? "",
        procurementType: invoiceDetails.procurement_type ?? "",
        treds_payment: invoiceDetails.treds_payment ?? "",
        totalTax: invoiceDetails.total_tax ?? "",
        billNo: invoiceDetails.bill_No ?? "",
        attachment: null,
        existingAttachmentName: invoiceDetails.attachment_name, // Store existing file name
        attachmentPreview: createDataUrl(invoiceDetails.attachment_data, invoiceDetails.attachment_mime),
        poContractDetails: {
          plant: invoiceDetails?.plant,
          created_by: invoiceDetails?.created_by,
          pending_with: invoiceDetails?.pending_with,
          responsible_person: invoiceDetails?.responsible_person,
        },
        documents: initialDocumentsData.map((doc) => {
          if (doc.description.toLowerCase().includes("labor")) {
            return {
              ...doc,
              file: null,
              existingFileName: invoiceDetails.labor_name,
            };
          }
          if (doc.description.toLowerCase().includes("work completion")) {
            return {
              ...doc,
              file: null,
              existingFileName: invoiceDetails.workcert_name,
            };
          }
          return { ...doc };
        }),
        gem_type: invoiceDetails?.gem_type ?? "",
        gem_attachment_name: invoiceDetails?.gem_attachment_name ?? "",
        othersAttachments: [...invoiceDetails?.othersAttachments]
      };

      setFormData((prev) => ({
        ...prev,
        ...commonDataFromApi,
      }));
    }
  }, [invoiceDetails, isEditMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (["grossAmount", "totalTax"]?.includes(e.target.name)) {
      setFormData((prev) => {
        const updatedValue = Number(e.target.value.replace(/,/g, ""));
        const totalTax = Number(prev?.totalTax.replace(/,/g, ""));
        const grossAmount = Number(prev?.grossAmount.replace(/,/g, ""));

        const updatedBillAmount = e.target.name === "grossAmount" ? String(totalTax + updatedValue) : String(grossAmount + updatedValue);
        const updatedNetPayable = String(Number(updatedBillAmount) - Number(prev?.advanceAdj));

        return {
          ...prev,
          [e.target.name]: e.target.value,
          billAmount: formatIndianCurrency(updatedBillAmount),
          netPayable: formatIndianCurrency(updatedNetPayable),
        };
      });
    } else if (e.target.name === "billAmount") {
      setFormData((prev) => {
        const advanceAdj = Number(prev?.advanceAdj.replace(/,/g, ""));
        const target = Number(e.target.value.replace(/,/g, ""));

        return {
          ...prev,
          [e.target.name]: e.target.value,
          netPayable: formatIndianCurrency(String(target - advanceAdj)),
        };
      });
    } else if (e.target.name === "advanceAdj") {
      setFormData((prev) => {
        const billAmount = Number(prev?.billAmount.replace(/,/g, ""));
        const target = Number(e.target.value.replace(/,/g, ""));
        return {
          ...prev,
          [e.target.name]: e.target.value,
          netPayable: formatIndianCurrency(String(billAmount - target)),
        };
      });
    } else {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }
  };

  const handleOtherFileChange = async (e: React.ChangeEvent<HTMLInputElement>, index: any) => {
    const file = e.target.files?.[0];
    const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

    if (file.size > maxSizeInBytes) {
      toast.error("File size exceeds 5 MB limit.");
      e.target.value = ""; // Clear the input
      return;
    }

    if (file && !["application/pdf"].includes(file.type)) {
      toast.error("Only PDF file are allowed!");
      e.target.value = ""; // Clear the input
      return;
    }

    var keys = ['attachment1', 'attachment2', 'attachment3', 'attachment4', 'attachment5'];



    if (file) {
      var fileObject = {
        file_key: keys[index],
        attachmentPreview: URL.createObjectURL(file),
        file_data: await fileToBase64(file),
        file_name: file?.name,
        file_type: file?.type,
        files: file,
        existingAttachmentName: true
      }
      var fileList = [...formData?.othersAttachments];
      fileList[index] = fileObject;
      setFormData((prev) => ({
        ...prev,
        othersAttachments: [...fileList],
      }));

    }
  };
  const handleOtherFileDownload = () => {

  }
  const onHandleDeleteRow = (index: any) => {
    var list = [...formData?.othersAttachments];
    list.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      othersAttachments: [...list],
    }));
  }

  const handleSelectChange = (name: string, valueObj: string | { label: string; value: string } | null) => {

    const finalValueToSet = typeof valueObj === "string" ? valueObj : valueObj ? valueObj.value : "";
    if (name === "invoiceForWhichLocation") {
      setFormData((prev) => ({
        ...prev,
        invoiceForWhichLocation: (valueObj as { label: string; value: string })?.value,
        location_name: (valueObj as { label: string; value: string })?.label,
      }));
    } else if (name === "procurementType") {
      setFormData((prev) => ({
        ...prev,
        [name]: finalValueToSet,
        divisionName: "",
        invoiceForWhichLocation: "",
        poContract: "",
        poContractDetails: null,
        pending_name: ''
      }));
    } else if (name === "throughGEM") {
      setFormData((prev) => ({
        ...prev,
        [name]: finalValueToSet,
        gemContactNo: "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: finalValueToSet, poContract: name === "procurementType" ? "" : formData.poContract }));
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, docId?: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

    if (file.size > maxSizeInBytes) {
      toast.error("File size exceeds 5 MB limit.");
      e.target.value = ""; // Clear the input
      return;
    }
    if (file && !["application/pdf"].includes(file.type)) {
      toast.error("Only PDF file are allowed!");
      e.target.value = ""; // Clear the input
      return;
    }

    const preview = URL.createObjectURL(file);
    if (docId) {
      setFormData((prev) => ({
        ...prev,
        // When a new file is chosen, it becomes the `file` and we clear `existingFileName`.
        documents: prev.documents.map((doc) => (doc.id === docId ? { ...doc, file, preview, existingFileName: null } : doc)),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        attachment: file,
        attachmentPreview: preview,
        existingAttachmentName: null,
      }));
      // Kick off smart extraction for the main invoice PDF
      if (file.name.toLowerCase().endsWith(".pdf")) {
        // setExtractState({ status: "uploading", extractedData: null, pdfFile: file, modalOpen: false, errorMsg: null });
        // const fd = new FormData();
        // fd.append("file", file);
        // uploadForExtraction(fd)
        //   .unwrap()
        //   .then((res) => {
        //     setExtractState((prev) => ({
        //       ...prev,
        //       status: "done",
        //       extractedData: {
        //         fields: res.fields,
        //         confidence: res.confidence,
        //         original_filename: res.filename,
        //       },
        //       modalOpen: true,
        //     }));
        //     toast.success("Invoice extracted successfully!");
        //   })
        //   .catch((err) => {
        //     setExtractState((prev) => ({ ...prev, status: "idle", pdfFile: null }));
        //     const msg = err?.data?.message || err?.message || "Could not extract invoice. Please fill details manually.";
        //     toast.error(msg);
        //   });
      }
    }
  };

  const handlePoSelectChange = (event: React.SyntheticEvent | null, newValue: any) => {

    setFormData((prev) => ({
      ...prev,
      poContract: newValue?.ebeln || "",
      approver_email: newValue?.email,
      poContractDetails: newValue,
      pending_name: newValue?.created_by
    }));
  };

  const vendorCodeToUse = isEditMode ? invoiceDetails?.vendor_code : localStorage.getItem("vendor_code");

  const onFinalSubmit = async (actionType: "submit" | "draft") => {
    if (actionType === "submit") {
      const errors = validateForm(formData);
      if (errors.length > 0) {
        // toast.error(errors[0]);
        errors.slice(0, 3).forEach((err) => toast.error(err));
        return;
      }
    }
    try {
      const payload = new FormData();
      // FIX: The data to submit is reliably taken from `formData`.
      const dataToSubmit = formData;

      payload.append("mode", actionType);
      payload.append("gem_option", dataToSubmit.throughGEM);
      // ... (rest of your submission logic is correct because it uses dataToSubmit)
      payload.append("gem_contact", dataToSubmit.gemContactNo);
      payload.append("bill_type", dataToSubmit.billType);
      payload.append("bill_category", dataToSubmit.billCategory);
      payload.append("procurement_type", dataToSubmit.procurementType);
      payload.append("treds_payment", dataToSubmit.treds_payment);
      payload.append("po_contract", dataToSubmit.poContract);
      payload.append("invoice_location", dataToSubmit.invoiceForWhichLocation);
      payload.append("location_name", dataToSubmit.location_name);
      payload.append("division_name", dataToSubmit.divisionName);
      payload.append("Remarks", dataToSubmit.remarks);
      payload.append("bill_No", dataToSubmit.billNo);
      payload.append("bill_date", dataToSubmit.billDate);
      payload.append("gross_amount", dataToSubmit.grossAmount);
      payload.append("total_tax", dataToSubmit.totalTax);
      payload.append("bill_amount", dataToSubmit.billAmount);
      payload.append("advance_adj", dataToSubmit.advanceAdj || "0");
      payload.append("net_payable", dataToSubmit.netPayable);
      payload.append("dak_no", dataToSubmit.dakNo);
      payload.append("bts_entry_date", dataToSubmit.btsEntryDate);
      payload.append("approver_email", dataToSubmit.approver_email);
      payload.append("pending_name", dataToSubmit.pending_name);
      payload.append("status", actionType === "submit" ? "Pending" : "Draft");

      if (dataToSubmit.attachment instanceof File) {
        payload.append("attachment", dataToSubmit.attachment);
      }
      const laborDoc = dataToSubmit.documents.find((doc) => doc.description.toLowerCase().includes("labor"));
      if (laborDoc && laborDoc.file instanceof File) {
        payload.append("labor_license", laborDoc.file);
      }
      const workCertDoc = dataToSubmit.documents.find((doc) => doc.description.toLowerCase().includes("work completion"));
      if (workCertDoc && workCertDoc.file instanceof File) {
        payload.append("work_cert", workCertDoc.file);
      }

      if (isEditMode && invoiceId) {
        payload.append("id", invoiceId);
      }

      for (let i = 0; i < dataToSubmit?.othersAttachments.length; i++) {
        if (dataToSubmit?.othersAttachments[i]?.files) {

          payload.append(dataToSubmit?.othersAttachments[i].file_key, dataToSubmit?.othersAttachments[i].files);
        }
      }
      payload.append("entry_type", "MM");
      payload.append("vendor_name", localStorage.getItem("vendor_name") || "");
      payload.append("vendor_code", localStorage.getItem("vendor_code") || "");
      payload.append("plant", dataToSubmit?.poContractDetails?.plant ?? "");
      payload.append("created_by", dataToSubmit?.poContractDetails?.created_by ?? "");
      payload.append("pending_with", dataToSubmit?.poContractDetails?.created_by ?? "");
      payload.append("responsible_person", dataToSubmit?.poContractDetails?.responsible_person ?? "");
      if (window.location.pathname.includes("edit-entry")) {
        payload.append("id", window.location.pathname.split("/")[2]);
      }

      const response = await handleInvoiceEntry({
        payload,
        isUpdate: isEditMode,
      }).unwrap();
      if (response.success) {
        if (actionType === "submit") {
          toast.success(`Selected items sent for approval! BTS No.: ${response?.bts_no}`);
          navigate("/bills");
          // setIsOpenFeedbackModal(true);
          // setSuccessModalOpen({
          //   open: true,
          //   title: `Selected items sent for approval! BTS No.: ${response?.bts_no}`,
          // });
        } else {
          navigate("/bills");
        }
      } else toast.error(`Server Error: ${response.message || "Failed to process."}`);
    } catch (err: any) {
      const errorMsg = (err?.data?.message || err?.message) || "An unknown error occurred during submission.";
      // console.error("Submission Failed:", err);
      toast.error(`Submission Failed: ${errorMsg}`);
    }
  };

  const handleSubmit = () => onFinalSubmit("submit");
  const handleDraftSave = () => onFinalSubmit("draft");

  useEffect(() => {
    if (location.pathname === "/add-new-entry") {
      setFormData({ ...baseInitialState });
    }
  }, [location.pathname]);

  if (isEditMode && isLoadingDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isEditMode && isDetailsError) {
    return (
      <Typography color="danger" sx={{ p: 4, textAlign: "center" }}>
        Error loading invoice details.
      </Typography>
    );
  }

  const isExtracting = extractState.status === "uploading";

  return (
    <>
      <ExtractionReviewModal
        open={extractState.modalOpen}
        extracted={extractState.extractedData}
        pdfFile={extractState.pdfFile}
        onConfirm={handleExtractionConfirm}
        onCancel={handleExtractionCancel}
      />
      <CssVarsProvider>
        <CssBaseline />
        <Box sx={{ display: "flex", minHeight: "92dvh", background: "white" }}>
          {/* <Header /> */}
          <Sidebar />

          <div className={`add-entry-outer ${!isEditMode ? "create-emp" : ""}`}>
            <Box
              sx={{
                p: { xs: 1.5, md: 2.5 },
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                pb: "130px",
              }}
            >
              {vendor_details && (
                <MaterialThemeProvider theme={muiThemeFinal}>
                  <Box
                    sx={{
                      p: 0,
                      bgcolor: "var(--joy-palette-background-surface)" /* Use Joy CSS var */,
                    }}
                  >
                    <VendorInfoCard vendor={responseVendorData} />
                  </Box>
                </MaterialThemeProvider>
              )}

              {formData?.btsNo !== "System Generated" && (
                <Typography level="title-lg" sx={sectionHeaderStyles} className="heading-1">
                  BTS No.: {formData?.btsNo.replace(/^0+/, "")}
                  <span className="info-text">(All values are in INR.)</span>
                </Typography>
              )}
              {/* <Typography level="body-xs" sx={{ color: "red" }}>
                All values are in INR.
              </Typography> */}
              <AttachmentTable
                formData={formData}
                btsNo={invoiceDetails?.bts_no}
                handleFileChange={handleFileChange}
                handleFileClick={handleFileDownload}
                isReadOnly={isReadOnly}
              />
              {isExtracting && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    mx: 0,
                    borderRadius: "sm",
                    bgcolor: "#eff6ff",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <CircularProgress size="sm" color="primary" />
                  <Typography level="body-sm" sx={{ color: "#1d4ed8" }}>
                    {extractState.status === "uploading"
                      ? "Uploading invoice for extraction…"
                      : "Reading invoice details — this may take a few seconds…"}
                  </Typography>
                </Box>
              )}

              <Sheet variant="outlined" sx={{ p: 0, borderRadius: "sm", overflow: "hidden" }}>
                <Typography level="title-lg" sx={sectionHeaderStyles} className="heading-1">
                  Header Details
                </Typography>
                {/* FIX: All input values now reliably read from `formData` to prevent crashes */}
                <Box
                  sx={{
                    p: 2,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                      md: "1fr 1fr 1fr",
                      lg: "1fr 1fr 1fr 1fr",
                    },
                    gap: 2,
                  }}
                  className="add-employee-form"
                >
                  <SelectInput
                    name="throughGEM"
                    required
                    label="Through Portal ?"
                    value={formData.throughGEM}
                    onChange={handleSelectChange}
                    options={throughGEMOptions}
                    placeholder="Select"
                    disabled={isReadOnly}
                  />
                  <TextInput
                    name="gemContactNo"
                    label="Portal Contract No"
                    value={formData.gemContactNo}
                    onChange={handleInputChange}
                    required={["gem", "tender_wizard", "nic"]?.includes(formData.throughGEM)}
                    placeholder={formData.throughGEM !== "no" ? `Enter ${formData.throughGEM ? formData.throughGEM : "GEM"} Contract No.` : ""}
                    disabled={isReadOnly || !["gem", "tender_wizard", "nic"]?.includes(formData.throughGEM)}
                  />
                  <SelectInput
                    name="billType"
                    label="Invoice Type"
                    required
                    value={formData.billType}
                    onChange={handleSelectChange}
                    options={billTypeOptions}
                    placeholder="Select"
                    disabled={isReadOnly}
                  />
                  <SelectInput
                    name="billCategory"
                    required
                    label="Invoice Category"
                    value={formData.billCategory}
                    onChange={handleSelectChange}
                    options={billCategoryInvoiceOptions}
                    placeholder="Select"
                    disabled={isReadOnly}
                  />
                  <SelectInput
                    name="procurementType"
                    label="Procurement Type"
                    required
                    value={formData.procurementType}
                    onChange={handleSelectChange}
                    options={procurementTypeOptions}
                    placeholder="Select"
                    disabled={isReadOnly}
                  />
                  <PoSelectionDropdown
                    vendorCode={vendorCodeToUse}
                    value={formData.poContract}
                    onChange={handlePoSelectChange}
                    disabled={isReadOnly}
                    user="vendor"
                    procurementType={formData.procurementType}
                    isEditMode={isEditMode}
                  />
                  <TextInput name="dakNo" label="DAK No." value={formData.dakNo} onChange={handleInputChange} placeholder="Enter DAK No." disabled={isReadOnly} />
                  <LocationDropdown formData={formData} handleSelectChange={handleSelectChange} isReadOnly={isReadOnly} isMM />
                  <DivisionDropdown formData={formData} handleSelectChange={handleSelectChange} isReadOnly={isReadOnly} isMM />

                  {<SelectInput
                    name="treds_payment"
                    label="Treds Payment"
                    value={formData.treds_payment}
                    onChange={handleSelectChange}
                    options={trendsCodeList}
                    placeholder="Select"
                    disabled={isReadOnly || tradsListOdata?.d?.results[0]?.Visible !== "X"}
                  />}

                  <TextareaInput
                    name="remarks"
                    label="Remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    placeholder="Enter remarks"
                    disabled={isReadOnly}
                    maxLength={500}
                    minRows={2}
                    formControlSx={{ gridColumn: { xs: "1", lg: "1 / span 4" } }}
                  />
                </Box>
              </Sheet>

              <BtsFinancialDetailsSection
                formData={formData}
                handleCurrencyOnBlur={(e) => handleCurrencyOnBlur(e, setFormData)}
                handleInputChange={handleInputChange}
                isReadOnly={isReadOnly}
                sectionHeaderStyles={sectionHeaderStyles}
              // isEditMode={isEditMode}
              />
              <OtherAttachments
                formData={formData}
                btsNo={invoiceDetails?.bts_no}
                handleFileChange={handleOtherFileChange}
                handleFileClick={handleFileDownload}
                onHandleDeleteRow={onHandleDeleteRow}
                isReadOnly={isReadOnly}
              />

              {/* {invoiceDetails?.approver_remarks && <><b>Remarks:</b> {invoiceDetails?.approver_remarks}</>} */}
              {invoiceDetails?.approver_remarks && (
                <div>
                  <b>Remarks for Rejection:</b> {invoiceDetails?.approver_remarks}
                </div>
              )}

              <Box sx={{ marginBottom: "50px" }}></Box>

              {!isReadOnly && (
                <Box
                  sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: "background.surface",
                    boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
                    p: 1.5,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                    zIndex: 1100,
                  }}
                >
                  <Button variant="soft" color="neutral" onClick={handleDraftSave} loading={isSubmitting} className="heading-4">
                    Draft Save
                  </Button>
                  <Button color="success" variant="soft" onClick={handleSubmit} loading={isSubmitting} className="heading-4">
                    {isEditMode ? "Submit" : "Submit for Approval"}
                  </Button>
                </Box>
              )}
            </Box>
          </div>
        </Box>
        <FeedbackModals open={isOpenFeedbackModal} setOpen={setIsOpenFeedbackModal} handleClose={() => navigate("/bills")} />
      </CssVarsProvider>
      <>
        <SuccessModal data={successModalOpen} setData={setSuccessModalOpen} handleClose={() => navigate("/bills")} />
      </>
    </>
  );
};
